import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav";
import Footer from "../components/Footer";
import API, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Users, Settings as Cog, Shield, FileText, Plus } from "lucide-react";
import logger from "../lib/logger";

export function AdminDashboard() {
  const [stats, setStats] = useState({});
  const { user } = useAuth();
  useEffect(() => { (async () => {
    try { setStats((await API.get("/dashboard")).data); }
    catch (e) { logger.error("Admin dashboard load failed:", e); }
  })(); }, []);
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-10 flex-1 w-full">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-muted-h mb-2">Administration</div>
          <h1 className="font-display text-4xl text-ink">Admin Dashboard</h1>
          <p className="text-ink-soft mt-1">{user?.first_name}, you have full control of the Heritage platform.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat label="Community Members" v={stats.members} testid="admin-stat-members" />
          <Stat label="Validators" v={stats.validators} testid="admin-stat-validators" />
          <Stat label="Heritage Staff" v={stats.staff} testid="admin-stat-staff" />
          <Stat label="Administrators" v={stats.administrators} testid="admin-stat-admins" />
          <Stat label="Total Users" v={stats.total_users} testid="admin-stat-users" />
          <Stat label="Total Submissions" v={stats.total_submissions} testid="admin-stat-subs" />
          <Stat label="Pending Reviews" v={stats.pending_reviews} accent testid="admin-stat-pending-reviews" />
          <Stat label="Pending Validations" v={stats.pending_validations} accent testid="admin-stat-pending-validations" />
          <Stat label="Approved Items" v={stats.approved_items} testid="admin-stat-approved" />
          <Stat label="Published Items" v={stats.published_items ?? stats.published} testid="admin-stat-published" />
          <Stat label="Assistance Requests" v={stats.assistance_requests} accent testid="admin-stat-assistance" />
          <Stat label="Overdue Tasks" v={stats.overdue_tasks} accent testid="admin-stat-overdue" />
        </div>

        <section className="grid md:grid-cols-4 gap-4">
          {[
            { icon: Users, title: "User Management", to: "/admin/users", testid: "nav-admin-users" },
            { icon: FileText, title: "All Submissions", to: "/staff/submissions", testid: "nav-admin-subs" },
            { icon: Cog, title: "System Settings", to: "/admin/settings", testid: "nav-admin-settings" },
            { icon: Shield, title: "Audit Log", to: "/admin/audit", testid: "nav-admin-audit" },
          ].map((c) => (
            <Link key={c.title} to={c.to} className="card-heritage hover:shadow-md hover:-translate-y-0.5 transition text-center" data-testid={c.testid}>
              <c.icon size={32} className="text-terracotta mx-auto mb-2" />
              <div className="font-display text-xl text-ink">{c.title}</div>
            </Link>
          ))}
        </section>
      </div>
      <Footer />
    </div>
  );
}

function Stat({ label, v, accent, testid }) {
  const val = v ?? 0;
  return (
    <div className={`card-heritage ${accent && val > 0 ? "!border-[var(--heritage-terracotta)]" : ""}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-h">{label}</div>
      <div className={`font-display text-3xl mt-2 ${accent && val > 0 ? "text-terracotta" : "text-ink"}`}>{val}</div>
    </div>
  );
}

export function UserManagement() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", role: "member" });
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await API.get("/users", { params: { q: q || undefined, role: role || undefined } });
    setUsers(data.items || []);
  };
  useEffect(() => { load(); }, [role]);

  const changeRole = async (uid, newRole) => {
    if (!window.confirm(`Change role to ${newRole}?`)) return;
    await API.put(`/users/${uid}`, { role: newRole }); load();
  };
  const toggleActive = async (u) => {
    await API.put(`/users/${u.id}`, { active: !u.active }); load();
  };
  const doCreate = async (e) => {
    e.preventDefault(); setErr("");
    try { await API.post("/users", form); setShowCreate(false); setForm({ email: "", password: "", first_name: "", last_name: "", role: "member" }); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-7xl mx-auto px-6 py-10 flex-1 w-full">
        <div className="flex items-end justify-between mb-6">
          <h1 className="font-display text-4xl text-ink">User Management</h1>
          <button onClick={() => setShowCreate(!showCreate)} className="btn-primary" data-testid="admin-new-user"><Plus size={16} /> New User</button>
        </div>

        {showCreate && (
          <form onSubmit={doCreate} className="card-heritage mb-6 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="First Name" required className="input-heritage" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="new-user-first" />
              <input placeholder="Last Name" required className="input-heritage" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="new-user-last" />
              <input type="email" placeholder="Email" required className="input-heritage" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
              <input type="password" placeholder="Password (min 6)" required minLength={6} className="input-heritage" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
              <select className="input-heritage" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
                <option value="member">Community Member</option>
                <option value="validator">Validator</option>
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {err && <div className="text-sm text-red-700">{err}</div>}
            <div className="flex gap-2"><button className="btn-primary" data-testid="new-user-submit">Create</button>
            <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button></div>
          </form>
        )}

        <div className="card-heritage mb-4 flex flex-wrap gap-3">
          <input type="search" placeholder="Search name/email/profile ID" className="input-heritage flex-1" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="user-search" />
          <select className="input-heritage max-w-xs" value={role} onChange={(e) => setRole(e.target.value)} data-testid="user-role-filter">
            <option value="">All Roles</option>
            <option value="member">Community Members</option>
            <option value="validator">Validators</option>
            <option value="staff">Staff</option>
            <option value="admin">Admins</option>
          </select>
        </div>

        <div className="card-heritage p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--heritage-parchment)] text-left text-xs uppercase tracking-wider text-muted-h">
              <tr><th className="px-3 py-3">Name</th><th className="px-3 py-3">Email</th><th className="px-3 py-3">Profile ID</th><th className="px-3 py-3">Role</th><th className="px-3 py-3">Active</th><th className="px-3 py-3">Last Login</th><th className="px-3 py-3">Actions</th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-heritage" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-3 text-ink">{u.first_name} {u.last_name}</td>
                  <td className="px-3 py-3 text-ink-soft">{u.email}</td>
                  <td className="px-3 py-3 font-mono text-xs text-muted-h">{u.profile_id}</td>
                  <td className="px-3 py-3">
                    <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} className="input-heritage py-1 text-xs" data-testid={`user-role-${u.id}`}>
                      <option value="member">Member</option>
                      <option value="validator">Validator</option>
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-3 py-3">
                    <button onClick={() => toggleActive(u)} className={`badge ${u.active ? "badge-approved" : "badge-denied"}`} data-testid={`user-toggle-${u.id}`}>
                      {u.active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-muted-h text-xs">{u.last_login ? new Date(u.last_login).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-3 text-right"><button onClick={() => toggleActive(u)} className="text-terracotta hover:underline text-xs">{u.active ? "Deactivate" : "Activate"}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function AdminSettings() {
  const [flags, setFlags] = useState({ information_gathering_mode: true, reminder_thresholds: {} });
  const [busy, setBusy] = useState(false);
  const [thresholds, setThresholds] = useState({
    validate_submission: 5, review_submission: 3,
    provide_additional_information: 7, assistance_request: 2,
  });
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data } = await API.get("/settings");
      setFlags(data);
      if (data.reminder_thresholds) setThresholds({ ...thresholds, ...data.reminder_thresholds });
    }
    catch (e) { logger.error("Settings load failed:", e); }
  })(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const next = !flags.information_gathering_mode;
      await API.put("/settings/information_gathering_mode", { value: next });
      setFlags({ ...flags, information_gathering_mode: next });
    } catch (e) {
      logger.error("Settings toggle failed:", e);
      alert("Could not update setting. Please try again.");
    }
    setBusy(false);
  };

  const saveThresholds = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(thresholds).map(([k, v]) => [k, Math.max(1, parseInt(v, 10) || 1)]));
      await API.put("/settings/reminder_thresholds", { value: clean });
      setThresholds(clean);
      setRunMsg("Thresholds saved.");
    } catch (e) {
      logger.error("Save thresholds failed:", e);
      alert("Could not save thresholds.");
    }
    setBusy(false);
  };

  const runNow = async () => {
    setRunning(true); setRunMsg("");
    try {
      const { data } = await API.post("/settings/reminders/run-now");
      setRunMsg(`Sweep complete — ${data.reminded} reminder(s) sent (${data.scanned} tasks scanned).`);
    } catch (e) { logger.error("Run reminders failed:", e); setRunMsg("Failed to run reminders."); }
    setRunning(false);
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-4xl mx-auto px-6 py-10 flex-1 w-full">
        <h1 className="font-display text-4xl text-ink mb-6">System Settings</h1>
        <div className="card-heritage">
          <h2 className="font-display text-2xl text-ink mb-2">Feature Flags</h2>
          <div className="border-t border-heritage pt-4 mt-4">
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="font-display text-lg text-ink">Information Gathering Mode</div>
                <p className="text-sm text-ink-soft mt-1 max-w-lg">
                  When enabled, public visitors can submit heritage information without creating an account.
                  Clear messaging explains that submission does not guarantee review or publication.
                  Turn OFF to require account registration for all submissions.
                </p>
              </div>
              <button onClick={toggle} disabled={busy} className={`px-4 py-2 rounded-full font-medium text-sm transition ${flags.information_gathering_mode ? "bg-[var(--heritage-terracotta)] text-white" : "bg-[var(--heritage-parchment)] text-ink"}`} data-testid="toggle-info-gathering">
                {flags.information_gathering_mode ? "Enabled" : "Disabled"}
              </button>
            </div>
          </div>
        </div>

        <div className="card-heritage mt-6">
          <h2 className="font-display text-2xl text-ink mb-2">Stale-Task Reminders</h2>
          <p className="text-sm text-ink-soft mb-4 max-w-2xl">
            After a task has been open longer than the threshold below, the platform will automatically
            create an in-app reminder for the assignee and prepare an email (respecting the current
            email mode). Reminders are de-duplicated within a 24-hour window per task.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { key: "validate_submission", label: "Validation requests (days)", testid: "thr-validate" },
              { key: "review_submission", label: "Staff reviews (days)", testid: "thr-review" },
              { key: "provide_additional_information", label: "Info requests unanswered (days)", testid: "thr-moreinfo" },
              { key: "assistance_request", label: "Assistance requests (days)", testid: "thr-assist" },
            ].map((f) => (
              <label key={f.key} className="block">
                <div className="text-xs uppercase tracking-widest text-muted-h mb-1">{f.label}</div>
                <input
                  type="number" min={1} max={365}
                  className="input-heritage w-full" value={thresholds[f.key] ?? ""}
                  onChange={(e) => setThresholds({ ...thresholds, [f.key]: e.target.value })}
                  data-testid={f.testid}
                />
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-4 items-center">
            <button onClick={saveThresholds} disabled={busy} className="btn-primary" data-testid="save-thresholds">
              Save Thresholds
            </button>
            <button onClick={runNow} disabled={running} className="btn-ghost" data-testid="run-reminders-now">
              {running ? "Running…" : "Run Reminders Now"}
            </button>
            {runMsg && <span className="text-sm text-ink-soft" data-testid="reminder-run-msg">{runMsg}</span>}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function AuditLog() {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => {
    try { setItems((await API.get("/audit")).data.items || []); }
    catch (e) { logger.error("Audit load failed:", e); }
  })(); }, []);
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-10 flex-1 w-full">
        <h1 className="font-display text-4xl text-ink mb-6">Audit Log</h1>
        <div className="card-heritage p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--heritage-parchment)] text-left text-xs uppercase tracking-wider text-muted-h">
              <tr><th className="px-4 py-3">When</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Target</th></tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-heritage" data-testid={`audit-row-${a.id}`}>
                  <td className="px-4 py-3 text-muted-h text-xs">{new Date(a.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-ink">{a.user_name || "System"}</td>
                  <td className="px-4 py-3 text-ink-soft">{a.action}</td>
                  <td className="px-4 py-3 text-muted-h text-xs font-mono">{a.target_type}:{a.target_id?.slice(0, 8)}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-h">No audit entries.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
}
